import { ProgramPolicy } from '@/types/Programs';
import { numToMoneyString } from '@/utilities/Money';
import { compactNumber } from '@/utilities/Number';
import {
  DEFAULT_COVERAGE_HEIGHT,
  DEFAULT_RECT_HEIGHT,
  DEFAULT_RECT_WIDTH,
  DEFAULT_RETENTION_HEIGHT,
  DEFAULT_STROKE_WIDTH,
  DEFAULT_TEXT_STYLE,
} from '@/utilities/tower/v2/Canvas';
import { fabric } from 'fabric';
import { percentage } from '@/utilities/Number';
import { fontFamily, FONT_SIZES, DisplayOptions, Label, DEFAULT_DISPLAY_LABELS } from '@/types/tower/Tower';
import Store from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import LabeledObjectBase from '../LabeledObjectBase';

export const Type = 'policy-rect';
export const Height = 150;

export enum Parts {
  Rect = 'rect-base',
  Labels = 'policy-labels',
  RetentionContainer = 'retention-container',
  CoverageContainer = 'coverage-container',
}

export enum LabelFormat {
  Default = 'default',
  Inline = 'inline',
}

const PolicyRect = fabric.util.createClass(LabeledObjectBase, {
  type: Type,
  snappable: true,
  policy: {} as ProgramPolicy,
  isPrimary: undefined,
  retentionEnabled: undefined,
  coverageTypeEnabled: undefined,
  labelFormat: LabelFormat.Default,

  initialize(opts: fabric.PolicyRectOptions) {
    opts = opts || {};
    delete opts.height; // corrects issue where PolicyLabels was getting PolicyRect Height

    const policy = opts?.policy;
    const objs = opts.objects || [];
    this.labels = Store.state.towerCanvasState.displayOptions;
    this.textStyle = opts.textStyle || DEFAULT_TEXT_STYLE;
    this.retentionEnabled = opts.retentionEnabled;
    this.isPrimary = policy.p_e_flag === 'Primary';
    this.coverageContainerStroke = opts.coverageContainerStroke;
    this.coverageContainerFill = opts.coverageContainerFill;
    this.textColor = opts.textColor ?? '#FFFFFF';
    this.labelFormat = opts.labelFormat || LabelFormat.Default;

    if (this.retentionEnabled === undefined) {
      this.retentionEnabled = policy.p_e_flag === 'Primary';
    }

    const partsMap = toMap(objs);

    // create base rect
    const rect = partsMap.get(Parts.Rect);

    let rectHeight = DEFAULT_RECT_HEIGHT;

    if (this.labels.coverage?.enabled && this.isPrimary) {
      rectHeight += DEFAULT_COVERAGE_HEIGHT;
    }

    if (this.retentionEnabled && this.isPrimary) {
      rectHeight += DEFAULT_RETENTION_HEIGHT;
    }

    const defaultRect = {
      type: Parts.Rect,
      height: rectHeight,
    };

    partsMap.set(Parts.Rect, createRect(rect ? { ...rect } : defaultRect));

    // create labels
    const policyLabels = partsMap.get(Parts.Labels);
    partsMap.set(
      Parts.Labels,
      policyLabels ? createLabelsGroup(policyLabels as fabric.PolicyLabels) : createLabelsGroup(policy, this.labels, undefined, this.labelFormat),
    );

    this.coverageTypeEnabled = this.labels.coverage?.enabled && this.isPrimary;

    // add retention container if policy is primary
    let retentionContainer = partsMap.get(Parts.RetentionContainer) as fabric.RetentionContainer;
    partsMap.set(
      Parts.RetentionContainer,
      createRetentionContainer(
        policy,
        retentionContainer
          ? { ...retentionContainer }
          : { type: Parts.RetentionContainer, visible: this.retentionEnabled },
        this.retentionEnabled,
      ),
    );

    if (policy.p_e_flag !== 'Primary') {
      partsMap.get(Parts.RetentionContainer)?.set('visible', false);
      this.retentionEnabled = false;
    }

    const coverageContainer = partsMap.get(Parts.CoverageContainer);
    partsMap.set(
      Parts.CoverageContainer,
      createCoverageContainer(
        policy,
        coverageContainer
          ? { ...coverageContainer }
          : { type: Parts.CoverageContainer, visible: this.coverageTypeEnabled },
        this.coverageTypeEnabled,
        this.retentionEnabled,
      ),
    );

    this.strokeUniform = true;
    
    // remove clipPath from opts so we can re-create it
    if (opts.clipPath) {
      delete opts.clipPath;
    }

    // initialize group (parent)
    this.callSuper('initialize', Array.from(partsMap.values()), { ...opts });

    // Create a clip rectangle that matches the bounds of the object
    // Expand the clipPath by strokeWidth to prevent clipping the stroke
    // Strokes extend outside the object bounds, so we need to account for this
    const containerRect = this._objects?.find((obj: fabric.Object) => obj.type === Parts.Rect);
    const currentStrokeWidth = containerRect?.get('strokeWidth') ?? 0;
    const clipRect = new fabric.Rect({
      left: 0,
      top: 0,
      width: this.width + currentStrokeWidth,
      height: this.height + currentStrokeWidth,
      originX: 'center',
      originY: 'center',
    });

    this.set({
      clipPath: clipRect,
    });

    // register event listeners
    this.on('scaling', () => {
      // manually trigger the scaling event for the children when the policyrect is scaling with an active selection
      if (this.group) {
        for (const obj of this._objects) {
          obj.fire('scaling');
        }
      }

      this._objects.filter((obj: fabric.Object) => obj.type === Parts.RetentionContainer)[0]?.resizeLabel();
      this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0]?.resizeLabel();
    });

    this.on('modified', () => {
      this._objects.filter((obj: fabric.Object) => obj.type === Parts.RetentionContainer)[0]?.resizeLabel();
      this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0]?.resizeLabel();

      // reorient the labels group to the center of the visible rect
      centerLabels.bind(this)();
    });

    this.on('added', () => {
      this.canvas.on('before:render', handleCanvasBeforeRender.bind(this));
    });

    this.on('removed', () => {
      // unregister event listeners
      this.off('added');
      this.off('modified');
      this.off('removed');
      this.off('scaling');
      this.off('mousedblclick');
      this.canvas.off('before:render', handleCanvasBeforeRender);
    });

    this.on('mousedblclick', () => {
      if (this.retentionEnabled) {
        this.editRetentionContainer();
      }
    });

    // resize child object labels
    for (const obj of this._objects) {
      if ([Parts.RetentionContainer, Parts.CoverageContainer].includes(obj.type)) {
        obj.resizeLabel();
      }
    }

    retentionContainer = partsMap.get(Parts.RetentionContainer) as fabric.RetentionContainer;
    if (retentionContainer) {
      // reinsert retention container into group when deselected
      retentionContainer.on('deselected', () => {
        // Make sure retention container doesn't already exist
        if (this._objects.filter((obj: fabric.Object) => obj.type === Parts.RetentionContainer).length > 0) return;

        const groupHeight = this.get('height');
        const groupScaleY = this.get('scaleY');

        // apply scale to new height, then reset scale to match group
        retentionContainer.set('height', retentionContainer.get('height')! * retentionContainer.get('scaleY')!);
        retentionContainer.set('scaleY', 1 / groupScaleY);
        retentionContainer.set('scaleX', 1); // reset scale to match group
        retentionContainer.set('originY', 'top');

        this.insertAt(retentionContainer, 2, false);

        // apply top offset, taking coverage container into account if exists
        const coverageContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0];
        //todo: fix this
        retentionContainer.set(
          'top',
          ((groupHeight * groupScaleY) / 2 -
            (getHeight<fabric.RetentionContainer>(retentionContainer) + getHeight<fabric.CoverageContainer>(coverageContainer))) /
            groupScaleY,
        );
        retentionContainer.set('left', 0);
        retentionContainer.resizeLabel();

        this.canvas.remove(retentionContainer);
        centerLabels.bind(this)();
        this.canvas.requestRenderAll();

        Store.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, undefined);
      });

      // set bounds of retention container
      retentionContainer.on('scaling', () => {
        if (!this.group && this.canvas.getActiveObject() === retentionContainer) {
          Store.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, 'retentionContainerSelected');

          const coverageContainer = this._objects.filter(
            (obj: fabric.Object) => obj.type === Parts.CoverageContainer,
          )[0];
          const policyLabels = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Labels)[0];

          const retentionBottom =
            retentionContainer.get('top')! +
            getHeight<fabric.RetentionContainer>(retentionContainer) * retentionContainer.get('scaleY')!;
          const groupHeight = this.get('height');
          const groupBottom = this.get('top')! + groupHeight * this.get('scaleY')!;

          // lock bottom of ret container to bottom of group
          if (retentionBottom != groupBottom + (coverageContainer?.get('top') ?? 0)) {
            retentionContainer.set(
              'scaleY',
              (groupBottom -
                getHeight<fabric.CoverageContainer>(coverageContainer) -
                retentionContainer.get('top')! +
                this.get('strokeWidth') / 2) /
                getHeight<fabric.RetentionContainer>(retentionContainer),
            );
          }

          const policyLabelsTop = fabric.util.transformPoint(
            new fabric.Point(0, policyLabels.top ?? 0),
            this.calcTransformMatrix(),
          ).y;
          const policyLabelsBottom = policyLabelsTop + policyLabels.height * policyLabels.scaleY;

          // lock top of ret container to bottom of policy labels
          if (retentionContainer.get('top')! < policyLabelsBottom) {
            // adjust scale to stop at bottom of policy labels
            retentionContainer.set(
              'scaleY',
              (groupBottom - getHeight<fabric.CoverageContainer>(coverageContainer) - policyLabelsBottom) /
                retentionContainer.height!,
            );
            retentionContainer.set('top', policyLabelsBottom);
          }
        }
      });
    }
  },

  editRetentionContainer(): void {
    Store.commit(StoreMutationType.SET_CANVAS_CHANGE_REASON, 'retentionContainerSelected');

    const retentionContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.RetentionContainer)[0];
    if (!retentionContainer) return;

    //Remove ret container from group to allow resizing
    this.remove(retentionContainer);
    this.canvas.add(retentionContainer);

    // TODO: add logic to stop bottom Y scaling
    retentionContainer.set('lockUniScaling', true);
    retentionContainer.set('lockScalingX', true);
    retentionContainer.set('lockMovementX', true);
    retentionContainer.set('lockMovementY', true);

    const coverageContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0];

    // rescale/reposition retention container to visually maintain original position
    retentionContainer.set('scaleY', 1);
    retentionContainer.set('scaleX', this.get('scaleX'));
    retentionContainer.set('originY', 'top');
    retentionContainer.set(
      'left',
      this.get('left') +
        (retentionContainer.get('width') * retentionContainer.get('scaleX') + this.get('strokeWidth')) / 2,
    );

    const groupHeight = this.get('height');
    const groupScaleY = this.get('scaleY');
    retentionContainer.set(
      'top',
      this.get('top') +
        groupHeight * groupScaleY -
        (retentionContainer.get('height') +
          (coverageContainer.visible ? coverageContainer?.get('height') ?? 0 : 0) -
          this.get('strokeWidth') / 2),
    );
    retentionContainer.resizeLabel();

    this.canvas.setActiveObject(retentionContainer);
    this.canvas.requestRenderAll();
  },

  getPolicyData(): ProgramPolicy {
    return this.policy;
  },

  setPolicyData(policy: ProgramPolicy) {
    this.policy = policy;
  },

  updatePolicyData(policy: ProgramPolicy) {
    this.policy = policy;
    if (this.policy.p_e_flag !== 'Primary') {
      this.retentionEnabled = false;
    }

    const policyLabels = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Labels)[0];
    this.insertAt(createLabelsGroup(this.policy, this.labels, policyLabels, this.labelFormat), 1, true);
    this.set('fontFamily', fontFamily(this.textStyle));
    this.set('textColor', this.textColor);
    this.resize();
    this.resizeLabel();
    this.centerLabels();
  },

  resizeLabel(): void {
    this.callSuper('resizeLabel');
    this._objects
      .filter((o: fabric.Object) => o.type === Parts.RetentionContainer)
      .forEach((o: fabric.RetentionContainer) => {
        o.resizeLabel();
      });
  },

  resize(): void {
    this.updateRetentionContainer();
    this.updateCoverageTypeContainer();
    this.setCoords();
    this.forEachObject((obj: fabric.Object) => {
      obj.setCoords();
    })
  },

  centerLabels: centerLabels,

  updateRetentionContainer(): void {
    const rco = this._objects.filter(
      (o: fabric.Object) => o.type === Parts.RetentionContainer,
    )[0] as fabric.RetentionContainer;
    if (!rco) return;

    rco.set('visible', this.retentionEnabled && this.policy.p_e_flag === 'Primary');

    if (this.policy.p_e_flag !== 'Primary') {
      rco.resizeLabel();
    } else if (this.policy && this.policy.p_e_flag === 'Primary') {
      rco.setLabel(this.policy);
      rco.resizeLabel();

      // update retention container position based on policy rect
      const coverageContainer = this._objects.filter(
        (obj: fabric.Object) => obj.type === Parts.CoverageContainer,
      )[0] as fabric.CoverageContainer;
      const groupHeight = this.get('height');
      const groupScaleY = this.get('scaleY');
      rco.set('height', rco.get('height'));
      rco.set('scaleY', 1 / groupScaleY);
      rco?.set(
        'top',
        ((groupHeight * groupScaleY) / 2 -
          ((rco.height ?? 0) + (coverageContainer.visible ? coverageContainer?.height ?? 0 : 0))) /
          groupScaleY,
      );
    }
  },

  updateCoverageTypeContainer(): void {
    const coverageContainer = this.getObjects().filter(
      (o: fabric.Object) => o.type === Parts.CoverageContainer,
    )[0] as fabric.CoverageContainer;
    coverageContainer?.setLabel(this.policy.coverageType ?? '');
    const isPrimary = this.policy.p_e_flag === 'Primary';
    const isDisplayEnabled = this.labels.coverage?.enabled;

    this.set({
      isPrimary,
    });

    coverageContainer?.set({
      visible: isPrimary && isDisplayEnabled,
    });
  },

  toObject() {
    return fabric.util.object.extend(this.callSuper('toObject'), {
      id: this.id,
      policy: this.policy,
      lockScalingFlip: this.lockScalingFlip,
      fontSize: this.fontSize,
      textColor: this.textColor,
      retentionEnabled: this.retentionEnabled,
      coverageTypeEnabled: this.coverageTypeEnabled,
      isPrimary: this.isPrimary,
      coverageContainerStroke: this.coverageContainerStroke,
      coverageContainerFill: this.coverageContainerFill,
      clipPath: this.clipPath,
      labelFormat: this.labelFormat,
    });
  },

  setDisplayLabels(visibleLabels: DisplayOptions) {
    this.labels = visibleLabels;
    const policyLabels = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Labels)[0];
    this.insertAt(createLabelsGroup(this.policy, this.labels, policyLabels, this.labelFormat), 1, true);
    this.set('fontFamily', fontFamily(this.textStyle));
    this.set('fontSize', this.fontSize);
    this.set('textColor', this.textColor);
    this.resizeLabel();
    this.updateLabels();
    this.toggleCoverageContainer(visibleLabels.coverage?.enabled);
  },

  updateLabels(fontSize?: number) {
    let space = this._objects[1]?._objects[0]?.get('top');
    this._objects[1]?._objects.forEach((obj: fabric.Object) => {
      obj.set('top', space);
      space += fontSize ?? this.fontSize;
    });
  },

  toggleCoverageContainer(isDisplayEnabled?: boolean) {
    let coverageContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0];
    const retentionContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.RetentionContainer)[0];
    const groupHeight = this.get('height');
    const groupScaleY = this.get('scaleY');

    if (isDisplayEnabled && this.isPrimary) {
      coverageContainer.set('visible', true);
      this.coverageTypeEnabled = true;
      coverageContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0];
      coverageContainer.set('scaleY', 1 / groupScaleY);
      coverageContainer.set('top', ((groupHeight * groupScaleY) / 2 - coverageContainer.height) / groupScaleY);
      coverageContainer.resizeLabel();
      retentionContainer?.set(
        'top',
        ((groupHeight * groupScaleY) / 2 - (retentionContainer.height + (coverageContainer?.height ?? 0))) /
          groupScaleY,
      );
    } else {
      coverageContainer.set('visible', false);
      this.coverageTypeEnabled = false;
      retentionContainer?.set('top', ((groupHeight * groupScaleY) / 2 - retentionContainer.height) / groupScaleY);
    }
    this.resize();
    this.canvas.requestRenderAll();
  },

  toggleRetention() {
    this.retentionEnabled = !this.retentionEnabled;
    this.resize();
    this.canvas.requestRenderAll();
  },

  toggleLabelFormat() {
    this.labelFormat = this.labelFormat === LabelFormat.Default ? LabelFormat.Inline : LabelFormat.Default;
    const displayOptions = Store.state.towerCanvasState.displayOptions;
    if (displayOptions) {
      this.setDisplayLabels(displayOptions);
      this.canvas.requestRenderAll();
    }
  },

  set: function <K extends keyof fabric.PolicyRect>(
    ...params: [K, fabric.PolicyRect[K]] | [Record<K, fabric.PolicyRect[K]>]
  ) {
    // options object
    if (params.length === 1) {
      const options = params[0];
      for (const prop in options) {
        this.setPolicyRectOptions(prop, options[prop]);
        /* eslint-disable no-fallthrough */
        switch (prop) {
          case 'opacity':
          case 'strokeWidth':
          case 'stroke':
          case 'fontFamily':
            break;
          default:
            this.callSuper('set', prop, options[prop]);
        }
      }
    }
    // key/value pair
    else if (params.length === 2) {
      const prop = params[0];
      const value = params[1];
      this.setPolicyRectOptions(prop, value);
      switch (prop) {
        case 'opacity':
        case 'strokeWidth':
        case 'stroke':
        case 'fontFamily':
          break;
        default:
          this.callSuper('set', prop, value);
      }
    }
  },

  setPolicyRectOptions<K extends keyof fabric.PolicyRect>(key: K, value: fabric.PolicyRect[K]) {
    switch (key) {
      case 'opacity': {
        this.callSuper('set', key, value);
        break;
      }
      case 'strokeWidth': {
        this.strokeWidth = value;

        const containerRect = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Rect && obj.visible)[0];
        const labels = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Labels && obj.visible)[0];
        const retentionContainer = this._objects.filter(
          (obj: fabric.Object) => obj.type === Parts.RetentionContainer && obj.visible,
        )[0];
        const coverageContainer = this._objects.filter(
          (obj: fabric.Object) => obj.type === Parts.CoverageContainer && obj.visible,
        )[0];

        const strokeWidth = containerRect.get('strokeWidth');
        const strokeWidthDiff = strokeWidth - value;
        const groupScaleX = this.get('scaleX');
        const groupScaleY = this.get('scaleY');

        const labelWidth = labels.get('width');
        const labelNewWidth = (labelWidth * groupScaleX + strokeWidthDiff * groupScaleX) / groupScaleX;
        labels.set('width', labelNewWidth);

        if (retentionContainer) {
          const retentionWidth = retentionContainer.get('width');
          const retentionNewWidth = (retentionWidth * groupScaleX + strokeWidthDiff * groupScaleX) / groupScaleX;
          retentionContainer.set('width', retentionNewWidth);
          retentionContainer.set('scaleY', 1 / groupScaleY);
          retentionContainer.resizeLabel();
        }

        if (coverageContainer) {
          const coverageWidth = coverageContainer.get('width');
          const coverageNewWidth = (coverageWidth * groupScaleX + strokeWidthDiff * groupScaleX) / groupScaleX;
          coverageContainer.set('width', coverageNewWidth);
          coverageContainer.set('scaleY', 1 / groupScaleY);
          coverageContainer.resizeLabel();
        }

        const rectWidth = containerRect.get('width');
        const rectHeight = containerRect.get('height');
        const rectNewWidth = (rectWidth * groupScaleX + strokeWidthDiff * groupScaleX) / groupScaleX;
        const rectNewHeight = (rectHeight * groupScaleY + strokeWidthDiff * groupScaleY) / groupScaleY;
        containerRect.set('width', rectNewWidth);
        containerRect.set('height', rectNewHeight);
        containerRect.set('strokeWidth', value);
        this.set('width', rectNewWidth);
        this.set('height', rectNewHeight);

        // Reset top to prevent horizontal movement
        const groupHeight = this.get('height');
        retentionContainer?.set(
          'top',
          ((groupHeight * groupScaleY) / 2 -
            (retentionContainer.height +
              (coverageContainer && coverageContainer.visible ? coverageContainer?.height ?? 0 : 0))) /
            groupScaleY,
        );
        coverageContainer?.set('top', ((groupHeight * groupScaleY) / 2 - coverageContainer.height) / groupScaleY);

        // Update clipPath to account for new strokeWidth
        // Strokes extend outside object bounds, so clipPath needs to be larger
        const newClipRect = new fabric.Rect({
          left: 0,
          top: 0,
          width: rectNewWidth + (value as number),
          height: rectNewHeight + (value as number),
          originX: 'center',
          originY: 'center',
        });
        this.clipPath = newClipRect;

        this.callSuper('set', key, value);
        break;
      }
      case 'stroke':
        //retention container calculations
        this.callSuper('set', key, value);
        break;
      case 'coverageContainerStroke': {
        const coverageContainer = this._objects.filter(
          (obj: fabric.Object) => obj.type === Parts.CoverageContainer && obj.visible,
        )[0];
        if (coverageContainer && value) {
          coverageContainer.set('coverageContainerStroke', value);
        }
        break;
      }
      case 'coverageContainerFill': {
        const coverageContainer = this._objects.filter(
          (obj: fabric.Object) => obj.type === Parts.CoverageContainer && obj.visible,
        )[0];
        if (coverageContainer && value) {
          coverageContainer.set('coverageContainerFill', value);
        }
        break;
      }
      case 'fontSize':
        this.updateLabels(value);
      case 'fontFamily': {
        this._objects[1]._objects.forEach((obj: fabric.Object) => {
          // @ts-expect-error fabric types are incorrect
          obj.set(key, value);
        });
        const retentionContainer = this._objects.filter(
          (obj: fabric.Object) => obj.type === Parts.RetentionContainer,
        )[0];
        retentionContainer?._objects.forEach((obj: fabric.Object) => {
          // @ts-expect-error fabric types are incorrect
          obj.set(key, value);
        });
        const coverageContainer = this._objects.filter((obj: fabric.Object) => obj.type === Parts.CoverageContainer)[0];
        coverageContainer?._objects.forEach((obj: fabric.Object) => {
          // @ts-expect-error fabric types are incorrect
          obj.set(key, value);
        });
        break;
      }
      case 'width':
      case 'height':
      case 'top':
      case 'left':
        break;
      default:
        this.callSuper('set', key, value);
    }
  },

  isPolicyLine(): boolean {
    return !!this.policy.policyLineId;
  },
});

// static fromObject function override
PolicyRect.fromObject = function (obj: fabric.Object, fn: (obj: fabric.Object) => void) {
  const policyRect = new PolicyRect(obj);
  if (fn) fn(policyRect);
  return policyRect;
};

// ------------------
// private functions
// ------------------
function centerLabels(this: fabric.PolicyRect) {
  const labelsGroup = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Labels)[0];
  const baseRect = this._objects.filter((obj: fabric.Object) => obj.type === Parts.Rect)[0];
  const retention = this._objects.filter(
    (obj: fabric.Object) => obj.type === Parts.RetentionContainer,
  )[0] as fabric.RetentionContainer;
  const coverage = this._objects.filter(
    (obj: fabric.Object) => obj.type === Parts.CoverageContainer,
  )[0] as fabric.CoverageContainer;

  const visibleRectHeight =
    baseRect.getScaledHeight() -
    getHeight<fabric.RetentionContainer>(retention, true) -
    getHeight<fabric.CoverageContainer>(coverage, true);
  labelsGroup.set('top', baseRect.get('top')! + visibleRectHeight / 2);
}

/**
 * Get the height of an object, taking into account its scale.
 * @param obj
 * @param scaled
 */
function getHeight<T extends fabric.Object>(obj: T, scaled: boolean = false): number {
  if (!obj || !obj.visible) return 0;

  if (scaled) {
    return obj.getScaledHeight();
  } else {
    return obj.height ?? 0;
  }
}

function createRect(opts: fabric.RectOptions): fabric.RectBase {
  const defaults = {
    type: opts.type,
    originX: 'center',
    opacity: 1,
    stroke: '#61BABA',
    strokeWidth: DEFAULT_STROKE_WIDTH,
    strokeDashArray: [0],
    strokeUniform: true,
    width: DEFAULT_RECT_WIDTH,
    height: DEFAULT_RECT_HEIGHT,
    lockScalingFlip: true,
    paintFirst: 'stroke',
    top: 0,
    left: 0,
  };

  return new fabric.RectBase({ ...defaults, ...opts });
}

function createRetentionContainer(
  policy: ProgramPolicy,
  opts: fabric.RectOptions,
  retentionEnabled: boolean,
): fabric.RetentionContainer {
  if (opts.top === undefined && retentionEnabled) {
    opts.top = DEFAULT_RECT_HEIGHT;
  }
  return new fabric.RetentionContainer(policy, opts);
}

function createCoverageContainer(
  policy: ProgramPolicy,
  opts: fabric.RectOptions,
  coverageTypeEnabled: boolean,
  retentionEnabled: boolean,
): fabric.LabeledObjectBase {
  if (opts.top === undefined && coverageTypeEnabled) {
    opts.top = retentionEnabled ? DEFAULT_RECT_HEIGHT + DEFAULT_RETENTION_HEIGHT : DEFAULT_RECT_HEIGHT; 
  }
  return new fabric.CoverageContainer(policy, opts);
}

function createText(text: string, opts: fabric.LabelOptions): fabric.PolicyLabel {
  const defaults = {
    fontSize: FONT_SIZES[1],
    originX: 'center',
    originY: 'center',
    textAlign: 'center',
    fill: 'white',
    strokeDashArray: [0],
    strokeUniform: true,
    lockScalingFlip: true,
  };

  return new fabric.PolicyLabel(text || '--', { ...defaults, ...opts });
}

function createLabelsGroup(
  ...params:
    | [policy: ProgramPolicy, labelsToDisplay: DisplayOptions, group?: fabric.PolicyLabels, labelFormat?: LabelFormat]
    | [group: fabric.PolicyLabels]
): fabric.PolicyLabels | undefined {
  if (params.length === 1) {
    const group = params[0];
    const objects = (group.objects as fabric.Text[]).map((o) => {
      return new fabric.Text(o.text || '', o);
    });
    return new fabric.PolicyLabels(objects, group);
  } else if (params.length >= 2) {
    const policy = params[0];
    const labelsToDisplay = params[1];
    const group = params[2];
    const labelFormat = params[3];
    let labels: fabric.Object[] = [];
    const labelText: string[] = [];
    let fontSize = FONT_SIZES[1];
    let space = 0;

    if (group?.group && (group.group as typeof LabeledObjectBase).fontSize) {
      fontSize = (group.group as typeof LabeledObjectBase).fontSize ?? FONT_SIZES[1];
    }

    if (labelsToDisplay[Label.CarrierLabel]?.enabled && policy.carrier !== undefined) {
      // Carrier + AM Best share a label to align horizontally
      if (labelsToDisplay[Label.AmBestLabel]?.enabled) {
        const amBestDisplayName =
          labelsToDisplay[Label.AmBestLabel].customName || DEFAULT_DISPLAY_LABELS.get(Label.AmBestLabel);
        const amBestText = `(${amBestDisplayName}: ${policy.carrierRating || '--'})`;

        const text = `${ getCarrierLabel(policy) || '' } ${amBestText}`;
        labelText.push(text)
        labels.push(
          createText(text, {
            label: Label.CarrierLabel,
            fontSize: fontSize,
          }),
        );
      } else {
        const text = getCarrierLabel(policy) || '--';
        labelText.push(text);
        labels.push(
          createText(text, { label: Label.CarrierLabel, fontSize: fontSize }),
        );
      }

      space += fontSize;
    }

    if (labelsToDisplay[Label.AggregateLimitAndAttachPointLabel]?.enabled) {
      let labelValue = compactNumber(policy.aggregateLimit);

      if (policy.coverageType === 'Work Comp') {
        labelValue = 'Statutory';
      } else if (policy.aggregateLimit) {
        if (policy.quotaShareTotal && policy.quotaShareTotal !== '0') {
          // Toggle display of Quota Share as percent, where percent = aggregateLimit / quotaShareTotal
          if (labelsToDisplay[Label.QuotaSharePercent]?.enabled) {
            labelValue = `${percentage((policy.aggregateLimit / parseInt(policy.quotaShareTotal)) * 100)}`;
          }

          labelValue += ` p/o ${compactNumber(parseInt(policy.quotaShareTotal))}`;
        }

        if (policy.p_e_flag !== 'Primary') {
          if (policy.attachmentPointValue) {
            labelValue += ` xs ${compactNumber(policy.attachmentPointValue)}`;
          }
        }
      }

      labelText.push(labelValue)
      labels.push(
        createText(`${labelValue}`, {
          label: Label.AggregateLimitAndAttachPointLabel,
          top: space,
          fontSize: fontSize,
        }),
      );
      
      space += fontSize;
    }

    if (labelsToDisplay[Label.OccurrenceLimitAndAttachPointLabel]?.enabled) {
      let labelValue = compactNumber(policy.occurrenceLimit);

      if (policy.coverageType === 'Work Comp') {
        labelValue = 'Statutory';
      } else if (policy.occurrenceLimit) {
        if (policy.quotaShareTotal && policy.quotaShareTotal !== '0') {
          // Toggle display of Quota Share as percent, where percent = occurrenceLimit / quotaShareTotal
          if (labelsToDisplay[Label.QuotaSharePercent]?.enabled) {
            labelValue = `${percentage((policy.occurrenceLimit / parseInt(policy.quotaShareTotal)) * 100)}`;
          }

          labelValue += ` p/o ${compactNumber(parseInt(policy.quotaShareTotal))}`;
        }

        if (policy.p_e_flag !== 'Primary') {
          if (policy.attachmentPointValue) {
            labelValue += ` xs ${compactNumber(policy.attachmentPointValue)}`;
          }
        }
      }

      labelText.push(labelValue)
      labels.push(
        createText(`${labelValue}`, {
          label: Label.OccurrenceLimitAndAttachPointLabel,
          top: space,
          fontSize: fontSize,
        }),
      );
      
      space += fontSize;
    }

    if (labelsToDisplay[Label.AggregateRateOnLineLabel]?.enabled) {
      if (policy.aggregateLimit !== undefined && policy.aggregateLimit > 0) {
        const text = `${percentage(((policy.policyPremium || 0) / policy.aggregateLimit) * 100)}`;
        labelText.push(text);
        labels.push(
          createText(text, {
            label: Label.AggregateRateOnLineLabel,
            top: space,
          }),
        );

      } else {
        labelText.push('--')
        labels.push(
          createText('--', {
            label: Label.AggregateRateOnLineLabel,
            top: space,
            fontSize: fontSize,
          }),
        );
      }

      space += fontSize;
    }

    if (labelsToDisplay[Label.OccurrenceRateOnLineLabel]?.enabled) {
      if (policy.occurrenceLimit !== undefined && policy.occurrenceLimit > 0) {
        const text = `${percentage(((policy.policyPremium || 0) / policy.occurrenceLimit) * 100)}`;
        labels.push(
          createText(text, {
            label: Label.OccurrenceRateOnLineLabel,
            top: space,
            fontSize: fontSize,
          }),
        );
        labelText.push(text);
      } else {
        labelText.push('--')
        labels.push(
          createText('--', {
            label: Label.OccurrenceRateOnLineLabel,
            top: space,
            fontSize: fontSize,
          }),
        );
      }

      space += fontSize;
    }

    if (labelsToDisplay[Label.PremiumLabel]?.enabled) {
      const text = numToMoneyString(policy.policyPremium) || '--';
      labelText.push(text)
      labels.push(
        createText(text, {
          label: Label.PremiumLabel,
          top: space,
          fontSize: fontSize,
        }),
      );

      space += fontSize;
    }

    if (labelFormat && labelFormat === LabelFormat.Inline) {
      labels = [createText(labelText.join(' | '), { label: Label.All, fontSize: fontSize})];
    }

    return new fabric.PolicyLabels(labels, {
      type: Parts.Labels,
      originX: 'center',
      originY: 'center',
      top: group?.top ?? 50,
      strokeUniform: true,
    });
  }
}

function getCarrierLabel(policy: ProgramPolicy){
  return policy.customCarrierName?.trim() || policy.carrierShortName || policy.carrier;
}

// Convert array of fabric objects to a map with its type as key
function toMap(objs: fabric.Object[]): Map<string, fabric.Object | undefined> {
  return objs.reduce((map, o) => {
    if (!o.type) {
      console.error('PolicyRect: toMap(): object is missing the "type" property');
      console.error(o);
      return map;
    }

    map.set(o.type, o);
    return map;
  }, new Map<string, fabric.Object>());
}

function handleCanvasBeforeRender(this: fabric.PolicyRect): void {
  centerLabels.bind(this)();
}

export default PolicyRect;
